import axios from "axios";

const api = axios.create({
  baseURL: "/", // same-origin FastAPI, e.g. https://info.ttanalytics.net/
});

let ws = null;

// Legacy subscribers (receive a LIMITED set of events).
// Prefer using subscribeTo(type, cb) below for precise events.
const subscribers = new Set();

// Typed subscribers: map of event type -> Set(callback)
const typedSubscribers = new Map();

// Only these event types will be forwarded to legacy subscribers.
const LEGACY_ALLOWED = new Set([
  "new_message",
  "port_update",
  "activation_update",
  "port_activity_update",
]);

// ---------------------------------------------------------------------------
// Simple in-memory cache (30s TTL) for select endpoints
// ---------------------------------------------------------------------------
const cache = new Map();
const CACHE_TTL = 30000; // 30 seconds

const getCached = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCache = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

const ensureTypeBucket = (type) => {
  if (!typedSubscribers.has(type)) {
    typedSubscribers.set(type, new Set());
  }
  return typedSubscribers.get(type);
};

/**
 * Normalize incoming WS frames into { type, payload }.
 */
const normalizeMessage = (data) => {
  if (data?.type && Object.prototype.hasOwnProperty.call(data, "payload")) {
    return { type: data.type, payload: data.payload };
  }
  if (data?.type && Object.prototype.hasOwnProperty.call(data, "data")) {
    return { type: data.type, payload: data.data };
  }
  if (data?.event && Object.prototype.hasOwnProperty.call(data, "payload")) {
    return { type: data.event, payload: data.payload };
  }
  if (data?.auto_cycle_update) {
    return { type: "auto_cycle_update", payload: data.auto_cycle_update };
  }
  if (data?.new_message) {
    return { type: "new_message", payload: data.new_message };
  }
  if (data?.port_update) {
    return { type: "port_update", payload: data.port_update };
  }
  if (data?.activation_update) {
    return { type: "activation_update", payload: data.activation_update };
  }
  if (data?.port_activity_update) {
    return { type: "port_activity_update", payload: data.port_activity_update };
  }
  return { type: "unknown", payload: data };
};

const notifyTyped = (type, payload, raw) => {
  const bucket = typedSubscribers.get(type);
  if (!bucket || bucket.size === 0) return;
  bucket.forEach((cb) => {
    try {
      cb(payload, { type, raw });
    } catch {
      /* noop */
    }
  });
};

const notifyLegacy = (type, payload, raw) => {
  if (!LEGACY_ALLOWED.has(type)) return;
  subscribers.forEach((cb) => {
    try {
      cb({ type, payload, raw });
    } catch {
      /* noop */
    }
  });
};

// Invalidate small caches when real-time updates arrive
const invalidateCachesForEvent = (type) => {
  if (type === "port_activity_update" || type === "activation_update") {
    cache.delete("port-activity-me");
    cache.delete("port-activity-staff");
    cache.delete("ports-me");
  }
};

const connectWebSocket = () => {
  if (ws) return;

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  ws = new WebSocket(`${protocol}//${window.location.host}/ws`);

  ws.onopen = () => {
    console.log("WebSocket Connected");
  };

  ws.onclose = () => {
    console.log("WebSocket Disconnected - Retrying in 5s");
    ws = null;
    setTimeout(connectWebSocket, 5000);
  };

  ws.onmessage = (event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }
    const { type, payload } = normalizeMessage(data);

    // Invalidate caches on activation-related events
    invalidateCachesForEvent(type);

    // Always notify typed (opt-in) subscribers for the exact type
    notifyTyped(type, payload, data);

    // Notify legacy subscribers only for whitelisted types
    notifyLegacy(type, payload, data);
  };
};

// Legacy: subscribe to a LIMITED set of WS events (see LEGACY_ALLOWED).
// Prefer using api.subscribeTo(type, cb) for precision.
api.subscribe = (callback) => {
  subscribers.add(callback);
  if (!ws) connectWebSocket();
  return () => subscribers.delete(callback);
};

// Preferred: subscribe to a specific event type
// Example: api.subscribeTo('auto_cycle_update', (state) => { ... })
api.subscribeTo = (type, callback) => {
  const bucket = ensureTypeBucket(type);
  bucket.add(callback);
  if (!ws) connectWebSocket();
  return () => {
    bucket.delete(callback);
    if (bucket.size === 0) {
      typedSubscribers.delete(type);
    }
  };
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const staffRole = localStorage.getItem("staff_role");
    const crmUserId = localStorage.getItem("crm_user_id");
    if (staffRole) config.headers["X-Staff-Role"] = staffRole;
    if (crmUserId) config.headers["X-CRM-User-Id"] = crmUserId;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("staff_role");
      localStorage.removeItem("crm_user_id");
      localStorage.removeItem("staff_name");
      localStorage.removeItem("email");
      localStorage.removeItem("user_code");
      sessionStorage.removeItem("vault_signup_authenticated");
      // Align with your app's login route
      window.location.href = "/userlogin";
    }
    return Promise.reject(error);
  }
);

// Connect WebSocket when token is present
if (localStorage.getItem("token")) {
  connectWebSocket();
}

/**
 * Identity used when stamping persona-check / similar actions.
 * Staff: display name (preferred) or email — never CRM numeric id.
 * User: user_code.
 */
export function getSessionStampWho() {
  if (typeof localStorage === "undefined") return "";
  const role = localStorage.getItem("role");
  if (role === "staff") {
    return (
      localStorage.getItem("staff_name") ||
      localStorage.getItem("email") ||
      ""
    );
  }
  return localStorage.getItem("user_code") || "";
}

export function getSessionStampedAs() {
  if (typeof localStorage === "undefined") return "Buyer";
  return localStorage.getItem("role") === "staff" ? "Staff" : "Buyer";
}

/* ========================================================================
 * Advice Passwords API helpers (staff/global)
 * ======================================================================= */
export async function fetchAdvicePasswords({ skip = 0, limit = 2000 } = {}) {
  const res = await api.get(`/advice-passwords`, {
    params: { skip, limit },
  });
  return res.data;
}

export async function createAdvicePassword(payload) {
  const res = await api.post(`/advice-passwords`, payload);
  return res.data;
}

export async function updateAdvicePassword(id, payload) {
  const res = await api.put(`/advice-passwords/${id}`, payload);
  return res.data;
}

export async function deleteAdvicePassword(id) {
  const res = await api.delete(`/advice-passwords/${id}`);
  return res.data;
}

export async function updateAdviceProxy(user_code, email, proxy) {
  const res = await api.put(
    `/advice-passwords/${encodeURIComponent(user_code)}/${encodeURIComponent(
      email
    )}/proxy`,
    { proxy }
  );
  return res.data;
}

/* ========================================================================
 * USER-scoped helpers (for the new UserDashboard)
 * ======================================================================= */
export async function fetchAdvicePasswordsMe({ skip = 0, limit = 500 } = {}) {
  const res = await api.get(`/advice-passwords/me`, {
    params: { skip, limit },
  });
  return res.data;
}

/** Buyer: request a replacement TM account for an advice-password row. */
export async function requestNewTmAccount(payload) {
  const res = await api.post(`/tm-account-requests/`, payload);
  return res.data;
}

/** Buyer: list own TM account requests (optionally by status). */
export async function fetchMyTmAccountRequests({ status = "pending" } = {}) {
  const res = await api.get(`/tm-account-requests/mine`, {
    params: status ? { status } : {},
  });
  return res.data;
}

/** Staff: list TM account requests (optionally by status). */
export async function fetchTmAccountRequests({ status = "pending" } = {}) {
  const res = await api.get(`/tm-account-requests/`, {
    params: status ? { status } : {},
  });
  return res.data;
}

/** Staff: create the replacement email and complete the request. */
export async function completeTmAccountRequest(requestId, payload) {
  const res = await api.post(`/tm-account-requests/${requestId}/complete`, payload);
  return res.data;
}

export async function createAdvicePasswordMe(payload) {
  const res = await api.post(`/advice-passwords/me`, payload);
  return res.data;
}

export async function updateAdvicePasswordMe(id, payload) {
  const res = await api.put(`/advice-passwords/me/${id}`, payload);
  return res.data;
}

export async function deleteAdvicePasswordMe(id) {
  const res = await api.delete(`/advice-passwords/me/${id}`);
  return res.data;
}

// NEW: save proxy for a specific advice password's (user_code, email)
export async function saveAdvicePasswordProxy(user_code, email, proxy) {
  const res = await api.put(
    `/advice-passwords/${encodeURIComponent(user_code)}/${encodeURIComponent(
      email
    )}/proxy`,
    { proxy }
  );
  return res.data;
}

// Get all unique sites (staff)
export async function fetchUniqueSites() {
  const res = await api.get(`/advice-passwords/sites`);
  return res.data;
}

// Get unique sites for current user
export async function fetchMyUniqueSites() {
  const res = await api.get(`/advice-passwords/sites/me`);
  return res.data;
}

export async function fetchPasswordsMe({ skip = 0, limit = 2000 } = {}) {
  const res = await api.get(`/passwords/me`, {
    params: { skip, limit },
  });
  return res.data;
}

/* ========================================================================
 * Gateway Management API helpers
 * ======================================================================= */
// Staff endpoints
export async function fetchGateways(userOwnedOnly = false) {
  const params = {};
  if (userOwnedOnly) {
    params.user_owned_only = true;
  }
  const res = await api.get(`/gateways`, { params });
  return res.data;
}

// Staff: Get ports for a specific gateway
export async function fetchPortsByGateway(gatewayId) {
  const res = await api.get(`/ports/`, { params: { gateway_id: gatewayId } });
  return res.data;
}

export async function listUsersWithGatewayAccess() {
  const res = await api.get(`/gateways/access/users`);
  return res.data;
}

export async function grantGatewayAccess(userCode) {
  const res = await api.put(`/gateways/access/${userCode}/grant`);
  return res.data;
}

export async function revokeGatewayAccess(userCode) {
  const res = await api.put(`/gateways/access/${userCode}/revoke`);
  return res.data;
}

export async function createGateway(payload) {
  const res = await api.post(`/gateways`, payload);
  return res.data;
}

export async function getGateway(gatewayId) {
  const res = await api.get(`/gateways/${gatewayId}`);
  return res.data;
}

export async function updateGateway(gatewayId, payload) {
  const res = await api.put(`/gateways/${gatewayId}`, payload);
  return res.data;
}

export async function deleteGateway(gatewayId) {
  const res = await api.delete(`/gateways/${gatewayId}`);
  return res.data;
}

// User endpoints
export async function getMyGateway() {
  const res = await api.get(`/gateways/me`);
  return res.data;
}

export async function listMyGateways() {
  const res = await api.get(`/gateways/me/list`);
  return res.data;
}

export async function checkGatewayAccess() {
  const res = await api.get(`/gateways/me/has-access`);
  return res.data;
}

export async function createMyGateway(payload) {
  const res = await api.post(`/gateways/me`, payload);
  return res.data;
}

export async function updateMyGateway(payload) {
  const res = await api.put(`/gateways/me`, payload);
  return res.data;
}

// User port management endpoints
export async function getMyGatewayPorts() {
  const res = await api.get(`/gateways/me/ports`);
  return res.data;
}

export async function getAssignableUsers() {
  const res = await api.get(`/gateways/me/assignable-users`);
  return res.data;
}

export async function createMyGatewayPort(payload) {
  const res = await api.post(`/gateways/me/ports`, payload);
  return res.data;
}

export async function updateMyGatewayPort(portId, payload) {
  const res = await api.put(`/gateways/me/ports/${portId}`, payload);
  return res.data;
}

export async function deleteMyGatewayPort(portId) {
  const res = await api.delete(`/gateways/me/ports/${portId}`);
  return res.data;
}

// Fetch last N messages for a specific port (NO CACHING)
export async function fetchMessagesByPort(portId, limit = 3) {
  const res = await api.get(`/received_sms/by-port/${portId}`, {
    params: { limit },
  });
  return res.data;
}

const MESSAGES_BY_PORTS_CHUNK = 400;

export async function fetchMessagesIndexedByPort(portsOrIds, limit = 10) {
  const ids = [];
  const seen = new Set();
  for (const item of portsOrIds || []) {
    const raw = item && typeof item === "object" ? item.id : item;
    const id = Number(raw);
    if (!Number.isFinite(id) || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  const indexed = {};
  for (const id of ids) indexed[id] = [];
  if (!ids.length) return indexed;

  for (let i = 0; i < ids.length; i += MESSAGES_BY_PORTS_CHUNK) {
    const chunk = ids.slice(i, i + MESSAGES_BY_PORTS_CHUNK);
    try {
      const res = await api.post("/received_sms/by-ports", {
        port_ids: chunk,
        limit,
      });
      const data = res.data || {};
      for (const id of chunk) {
        indexed[id] = data[id] || data[String(id)] || [];
      }
    } catch {
      // leave this chunk as empty arrays
    }
  }
  return indexed;
}

// Cached: fetch ports for the logged-in user
export async function fetchPortsMe({ skipCache = false } = {}) {
  const cacheKey = "ports-me";
  if (skipCache) {
    cache.delete(cacheKey);
  } else {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const response = await api.get("/ports/me");
  setCache(cacheKey, response.data);
  return response.data;
}

/* ========================================================================
 * Gateway + Activation (same-origin; backend routes to gw-1/gw-2)
 * ======================================================================= */

// Strict "gw-<digits>" from any label/id; returns undefined if it can't
function sanitizeGatewayLabel(label) {
  if (!label) return undefined;
  const str = String(label).trim();
  const m = str.match(/(\d+)/);
  if (!m) return undefined;
  return `gw-${m[1]}`;
}

// Always hit our own backend; include gateway_id so backend uses the right box.
export async function activatePortById(portId, phoneNumber, gatewayLabel) {
  // invalidate relevant caches
  cache.delete("ports-me");
  cache.delete("port-activity-me");

  const body = {};
  if (phoneNumber) body.number = phoneNumber;

  const cleanGateway = sanitizeGatewayLabel(gatewayLabel);
  if (cleanGateway) body.gateway_id = cleanGateway; // e.g. "gw-2"

  const res = await api.post(`/api/ports/${portId}/activate`, body);
  return res.data;
}

/** STAFF: Query SIM number on an active port (by port_id). */
export async function getNumber(portId) {
  const res = await api.post(`/ports/${portId}/get-number`);
  return res.data;
}

/** STAFF: Query SIM credit balance on an active port (by port_id). */
export async function getBalance(portId) {
  const res = await api.post(`/ports/${portId}/get-balance`);
  return res.data;
}

/** STAFF: Request PAC code SMS from an active port (by port_id). */
export async function requestPacCode(portId) {
  const res = await api.post(`/ports/${portId}/request-pac-code`);
  return res.data;
}

/** Buyer or staff: request Convert to TT OTP for a number in a port. */
export async function requestSimConversion(portId) {
  const res = await api.post(`/ports/${portId}/request-sim-conversion`, {
    requested_by: getSessionStampWho(),
  });
  return res.data;
}

/** Buyer or staff: request Convert to TT OTP for multiple numbers (one request id). */
export async function requestSimConversionBulk(portIds) {
  const res = await api.post(`/ports/request-sim-conversion-bulk`, {
    port_ids: portIds,
    requested_by: getSessionStampWho(),
  });
  return res.data;
}

/** Buyer or staff: look up a conversion request (single or bulk) by id. */
export async function fetchSimConversionRequest(requestId) {
  const res = await api.get(`/ports/sim-conversion-requests/${requestId}`);
  return res.data;
}

// Profile for address + DOB
export async function fetchMe() {
  const res = await api.get(`/users/me`);
  return res.data;
}

/* ========================================================================
 * Port Activity Cache + Loop
 * ======================================================================= */

// USER: get activity for the logged-in user's ports (cached)
export async function fetchPortActivity(gatewayId) {
  const cacheKey = gatewayId
    ? `port-activity-me:${gatewayId}`
    : "port-activity-me";

  const cached = getCached(cacheKey);
  if (cached) return cached;

  const res = await api.get(`/port-activity/me`, {
    params: gatewayId ? { gateway_id: gatewayId } : {},
  });

  setCache(cacheKey, res.data);
  return res.data;
}

// STAFF: get the entire activity cache (GLOBAL). Use only in staff UIs.
export async function fetchPortActivityAll() {
  const res = await api.get(`/port-activity`);
  return res.data;
}

// USER: Sync hardware status → PortActivation rows + PortActivity cache.
// Idempotent — safe to call whenever the dashboard looks stale.
export async function syncPortsFromHardware() {
  cache.delete("port-activity-me");
  cache.delete("port-activity-staff");
  const res = await api.post(`/port-activity/sync-from-hardware`);
  return res.data;
}

// Start/stop the activation/verification background loops (STAFF)
export async function startPortActivityLoop() {
  const res = await api.post(`/api/activation/start-loop`);
  return res.data;
}

export async function stopPortActivityLoop() {
  const res = await api.post(`/api/activation/stop-loop`);
  return res.data;
}

/* ========================================================================
 * Gateway + Status helpers
 * ======================================================================= */

// NEW: list available gateways (multi-gateway aware)
export async function fetchGatewayList() {
  const res = await api.get("/api/gateway-list");
  const raw = res.data?.gateways || [];

  // normalize to [{id: "gw-1", name: "gw-1"}, ...]
  return raw.map((gw) => {
    if (typeof gw === "string") {
      return { id: gw, name: gw };
    }
    return {
      id: gw.id ?? gw.name ?? gw,
      name: gw.name ?? gw.id ?? gw,
    };
  });
}

// NEW (numeric IDs from DB): PortManagement should prefer this.
export async function fetchGatewayListFromDB() {
  const response = await api.get("/api/gateways/db");
  // response.data will be [{id: 1, name: "gw-1", ...}, ...]
  return response.data;
}

/* ========================================================================
 * User Signup API helpers
 * ======================================================================= */
export async function fetchUserSignupList() {
  const res = await api.get(`/users-signup`);
  return res.data;
}

// STAFF: Switch active slot on gateway (direct device command). Port format e.g. "1.03", "2.16".
export async function staffSwitchPort(gatewayId, portStr) {
  const res = await api.post("/api/port_switch/", {
    ports: portStr,
    gateway_id: gatewayId || undefined,
  });
  // Invalidate status cache so next refresh shows updated active slot
  cache.delete(gatewayId ? `gateway-status:${gatewayId}` : "gateway-status");
  return res.data;
}

// Invalidate gateway status cache so next fetch returns fresh data (e.g. for user gateway refresh).
export function invalidateGatewayStatusCache(gatewayId) {
  cache.delete(gatewayId ? `gateway-status:${gatewayId}` : "gateway-status");
}

// UPDATED: Use cached gateway status instead of direct gateway calls,
// and allow optional gatewayId so UI can ask "give me gw2".
export async function fetchPortStatusCached(gatewayId) {
  const cacheKey = gatewayId
    ? `gateway-status:${gatewayId}`
    : "gateway-status";

  const cached = getCached(cacheKey);
  if (cached) return cached;

  const res = await api.get("/api/port-status", {
    params: gatewayId ? { gateway_id: gatewayId } : {},
  });

  const data = res.data;
  setCache(cacheKey, data);
  return data;
}

export default api;
